import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Heart, ArrowRight } from "lucide-react";
import { useCart } from "@/lib/cart";

const CartDrawer = () => {
  const { items, remove, isOpen, closeCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [isOpen]);

  const book = () => {
    closeCart();
    navigate("/book");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-[75] bg-kiki-ink/30 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-full max-w-md bg-[#FAF5EC] border-l border-kiki-line flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-kiki-line">
              <p className="flex items-center gap-2 font-display text-2xl text-kiki-ink">
                <Heart className="w-5 h-5 text-rose-500 fill-rose-500" /> Saved
              </p>
              <button
                data-testid="cart-close-btn"
                onClick={closeCart}
                aria-label="Close saved items"
                className="w-10 h-10 rounded-full border border-kiki-line bg-white flex items-center justify-center text-kiki-ink hover:text-kiki-gold hover:border-kiki-gold/40 transition-colors duration-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length > 0 && (
                <button
                  data-testid="cart-book-card"
                  onClick={book}
                  className="w-full text-left rounded-2xl bg-kiki-ink text-[#FAF5EC] p-6 mb-6 group hover:bg-[#3a2d26] transition-colors duration-300"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#FAF5EC]/60 font-semibold">Next step</p>
                  <div className="flex items-center justify-between gap-3 mt-2">
                    <p className="font-display text-2xl leading-tight">Book a free consultation</p>
                    <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                  <p className="text-sm text-[#FAF5EC]/70 font-light mt-1.5">
                    Talk to Bhakti about your saved items. Pick a time, we'll bring the plan.
                  </p>
                </button>
              )}

              {items.length === 0 ? (
                <div data-testid="cart-empty" className="text-center pt-16">
                  <p className="font-display text-2xl text-kiki-ink">Nothing saved yet</p>
                  <p className="text-sm text-kiki-muted font-light mt-2">
                    Tap the heart on any service or product bundle, then book a free consultation to discuss it.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item, i) => (
                    <div
                      key={item.name}
                      data-testid={`cart-item-${i}`}
                      className="flex items-center justify-between gap-3 bg-white border border-kiki-line rounded-xl px-5 py-4"
                    >
                      <div>
                        <p className="font-outfit font-medium text-kiki-ink text-sm">{item.name}</p>
                        <p className="text-xs text-kiki-gold font-outfit font-semibold mt-0.5">{item.price}</p>
                      </div>
                      <button
                        data-testid={`cart-remove-${i}`}
                        onClick={() => remove(item.name)}
                        aria-label={`Remove ${item.name}`}
                        className="w-8 h-8 rounded-full border border-kiki-line flex items-center justify-center text-kiki-muted hover:text-kiki-gold hover:border-kiki-gold/40 transition-colors duration-300"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-kiki-line px-6 py-4">
              <p className="text-[11px] text-kiki-muted font-light text-center">
                No online payment — we finalise services, products & pricing together on a free consultation call.
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
